import api from './api';

/**
 * Permission Service
 * Handles all permission-related API calls
 */
export const permissionService = {
  /**
   * Get all permissions
   */
  getAllPermissions: async () => {
    return await api.get('/permissions');
  },

  /**
   * Get permissions by resource
   */
  getPermissionsByResource: async (resource) => {
    return await api.get(`/permissions/resource/${resource}`);
  },

  /**
   * Get current user's permissions
   */
  getMyPermissions: async () => {
    return await api.get('/admin/my-permissions');
  },

  /**
   * Get user permissions by user ID (admin only)
   */
  getUserPermissions: async (userId) => {
    return await api.get(`/admin/users/${userId}/permissions`);
  },

  /**
   * Check if user has specific permission
   * Supports both object format {customer: ['read', 'create']} 
   * and array format [{resource: 'customer', action: 'read'}]
   */
  hasPermission: (permissions, resource, action) => {
    if (!permissions) return false;
    
    // Handle object format: {customer: ['read', 'create'], ...}
    if (typeof permissions === 'object' && !Array.isArray(permissions)) {
      return permissions[resource]?.includes(action) || false;
    }
    
    // Handle array format: [{resource: 'customer', action: 'read'}, ...]
    if (Array.isArray(permissions)) {
      return permissions.some(
        p => p.resource === resource && p.action === action
      );
    }
    
    return false;
  },

  /**
   * Check if user has any of the specified permissions
   * Supports both object and array formats
   */
  hasAnyPermission: (permissions, permissionChecks) => {
    if (!permissions) return false;
    
    // Handle object format
    if (typeof permissions === 'object' && !Array.isArray(permissions)) {
      return permissionChecks.some(
        ({ resource, action }) => permissions[resource]?.includes(action)
      );
    }
    
    // Handle array format
    if (Array.isArray(permissions)) {
      return permissionChecks.some(
        ({ resource, action }) => 
          permissions.some(p => p.resource === resource && p.action === action)
      );
    }
    
    return false;
  },

  /**
   * Check if user has all of the specified permissions
   * Supports both object and array formats
   */
  hasAllPermissions: (permissions, permissionChecks) => {
    if (!permissions) return false;
    
    // Handle object format
    if (typeof permissions === 'object' && !Array.isArray(permissions)) {
      return permissionChecks.every(
        ({ resource, action }) => permissions[resource]?.includes(action)
      );
    }
    
    // Handle array format
    if (Array.isArray(permissions)) {
      return permissionChecks.every(
        ({ resource, action }) =>
          permissions.some(p => p.resource === resource && p.action === action)
      );
    }
    
    return false;
  },

  /**
   * Group permissions by resource
   * Supports both object and array formats
   */
  groupByResource: (permissions) => {
    if (!permissions) return {};
    
    // Already in object format
    if (typeof permissions === 'object' && !Array.isArray(permissions)) {
      return permissions;
    }
    
    // Convert array format to object format
    if (Array.isArray(permissions)) {
      return permissions.reduce((grouped, permission) => {
        const resource = permission.resource;
        if (!grouped[resource]) {
          grouped[resource] = [];
        }
        grouped[resource].push(permission.action);
        return grouped;
      }, {});
    }
    
    return {};
  }
};
